/**
 * Waits for PowerSync's upload queue (`ps_crud`) to EMPTY OUT.
 *
 * local-first → REST seam (F4-D): when a flow writes via the write-path
 * (sync) and immediately calls a REST endpoint that READS those rows on the
 * server (e.g. adding schedule slots → "generate sessions"), the REST call can
 * outrun the upload and see an empty state. This helper drains the queue
 * before the REST call so the server sees what the user sees.
 *
 * Returns `true` if the queue emptied within the deadline; `false` if it
 * expired (offline, backend down…). The caller decides whether to proceed
 * anyway — the endpoint remains the authority.
 */
import type { AbstractPowerSyncDatabase } from "@powersync/web";

const POLL_INTERVAL_MS = 150;

export async function waitForUploadQueueDrain(
  ps: AbstractPowerSyncDatabase,
  timeoutMs = 10_000,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const rows = await ps.getAll<{ pending: number }>(
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- SQL, no UI
      "SELECT count(*) AS pending FROM ps_crud",
    );
    if ((rows[0]?.pending ?? 0) === 0) {
      return true;
    }
    if (Date.now() >= deadline) {
      return false;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
